import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Tabs,
  Tab,
  Stack,
  Fade,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Store } from '@mui/icons-material';
import ProductCard from './ProductCard';
import { productAPI, categoryAPI } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';

const ShopSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['All Products']);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userCountry } = useCurrency();

  useEffect(() => {
    fetchCategoriesAndProducts();
  }, []);

  useEffect(() => {
    if (selectedCategory !== 'All Products') {
      fetchProductsByCategory(selectedCategory);
    } else {
      fetchProducts();
    }
  }, [selectedCategory]);

  const fetchCategoriesAndProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const subCatResponse = await categoryAPI.getAllSubCategories();
      if (subCatResponse.success && subCatResponse.data) {
        const subCats = subCatResponse.data;
        setSubCategories(subCats);
        const categoryNames = ['All Products', ...subCats.map(cat => cat.name)];
        setCategories(categoryNames);
      }

      await fetchProducts();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAllProducts({ limit: 100, userCountry: userCountry });

      if (response.success && response.data) {
        const transformedProducts = response.data.map(product => ({
          id: product._id,
          name: product.name,
          category: product.subCategory?.name || 'Uncategorized',
          price: product.variants?.[0]?.sellingPrice || 0,
          mrp: product.variants?.[0]?.mrp || 0,
          image: product.images?.[0] || 'https://via.placeholder.com/500',
          description: product.description || '',
          benefits: product.attributes?.map(attr => attr.name) || [],
          featured: true,
          slug: product.slug,
          variants: product.variants || [],
          stock: product.variants?.[0]?.stock || 0,
        }));

        setProducts(transformedProducts);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsByCategory = async (categoryName) => {
    try {
      setLoading(true);

      const subCat = subCategories.find(cat => cat.name === categoryName);
      if (!subCat) {
        await fetchProducts();
        return;
      }

      const response = await productAPI.getAllProducts({
        subCategory: subCat._id,
        limit: 100,
        userCountry: userCountry
      });

      if (response.success && response.data) {
        const transformedProducts = response.data.map(product => ({
          id: product._id,
          name: product.name,
          category: product.subCategory?.name || 'Uncategorized',
          price: product.variants?.[0]?.sellingPrice || 0,
          mrp: product.variants?.[0]?.mrp || 0,
          image: product.images?.[0] || 'https://via.placeholder.com/500',
          description: product.description || '',
          benefits: product.attributes?.map(attr => attr.name) || [],
          featured: true,
          slug: product.slug,
          variants: product.variants || [],
          stock: product.variants?.[0]?.stock || 0,
        }));

        setProducts(transformedProducts);
      }
    } catch (err) {
      console.error('Error fetching products by category:', err);
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const filteredProducts = products;

  return (
    <Box
      id="shop"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #F5F2EB 0%, #FFFFFF 50%, #F8F6F1 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={6}>
          {/* Section Header */}
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 60,
                    height: 1,
                    bgcolor: 'primary.main',
                    opacity: 0.5,
                  }}
                />
                <Store sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box
                  sx={{
                    width: 60,
                    height: 1,
                    bgcolor: 'primary.main',
                    opacity: 0.5,
                  }}
                />
              </Stack>

              <Typography
                className='font2'
                variant="h2"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Our Wellness Shop
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 700,
                  mx: 'auto',
                  lineHeight: 1.8,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
              >
                Discover our curated collection of premium Ayurvedic products, carefully selected
                to support your wellness journey
              </Typography>
            </Box>
          </Fade>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Category Tabs */}
          {!loading && (
            <Fade in timeout={1000}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tabs
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 600,
                      fontSize: { xs: '0.85rem', md: '0.95rem' },
                      textTransform: 'none',
                      minWidth: { xs: 'auto', md: 120 },
                      px: { xs: 2, md: 3 },
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: 'primary.main',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'primary.main',
                      height: 3,
                    },
                  }}
                >
                  {categories.map((category) => (
                    <Tab key={category} label={category} value={category} />
                  ))}
                </Tabs>
              </Box>
            </Fade>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          )}

          {/* Products Grid */}
          {!loading && (
            <Fade in timeout={1200}>
              <Grid container spacing={4} className='prd-grid'>
                {filteredProducts.map((product, index) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id} className='grid-cst'>
                    <Fade in timeout={800 + index * 100}>
                      <Box>
                        <ProductCard product={product} />
                      </Box>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Fade>
          )}

          {/* Empty State */}
          {!loading && filteredProducts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No products found in this category
              </Typography>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ShopSection;
